import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * "Notify me" drawer for coming-soon products.
 *
 * On submit it sends the marketing consent to the Datajet task (theme setting
 * `datajet_script_id`), then tracks the Klaviyo "Subscribed to Launch" event (theme setting
 * `klaviyo_company_id`). Request bodies match the legacy ANZ theme so the existing Datajet task
 * and Klaviyo flows keep working. Success shows only when the Datajet call succeeds (or, with no
 * Datajet task configured, when Klaviyo accepts the event).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} email
 * @property {HTMLElement} emailError
 * @property {HTMLInputElement} phone
 * @property {HTMLInputElement} emailConsent
 * @property {HTMLInputElement} smsConsent
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} formError
 * @property {HTMLElement} success
 *
 * @extends DialogComponent
 */
class ComingSoonNotify extends DialogComponent {
  requiredRefs = [
    'dialog',
    'form',
    'email',
    'emailError',
    'phone',
    'emailConsent',
    'smsConsent',
    'submit',
    'formError',
    'success',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#reset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#reset);
  }

  /** @param {SubmitEvent} event */
  async handleSubmit(event) {
    event.preventDefault();

    const { email, emailError, submit, formError } = this.refs;
    const emailValue = email.value.trim();
    const emailValid = emailValue !== '' && email.checkValidity();

    formError.hidden = true;
    emailError.hidden = emailValid;
    email.setAttribute('aria-invalid', String(!emailValid));
    if (!emailValid) {
      email.focus();
      return;
    }

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    const consentSaved = await this.#updateMarketingConsent(emailValue);
    const eventTracked = await this.#trackSubscribedEvent(emailValue);
    const succeeded = this.dataset.taskUrl ? consentSaved : eventTracked;

    submit.disabled = false;
    submit.removeAttribute('aria-busy');

    if (!succeeded) {
      formError.hidden = false;
      return;
    }

    this.refs.form.hidden = true;
    this.refs.success.hidden = false;
    this.refs.success.focus();
  }

  /**
   * Adds the market's dialling code unless the customer typed an international number.
   * An empty field sends `null`.
   *
   * @returns {string | null}
   */
  #phoneNumber() {
    const raw = this.refs.phone.value.replace(/[\s().-]/g, '');
    if (!raw) return null;
    if (raw.startsWith('+')) return raw;
    return `${this.dataset.dialCode}${raw.replace(/^0+/, '')}`;
  }

  /**
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async #updateMarketingConsent(email) {
    const { taskUrl, productId } = this.dataset;
    if (!taskUrl) return false;

    try {
      const response = await fetch(taskUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          phone: this.#phoneNumber(),
          email_subscribed: this.refs.emailConsent.checked,
          sms_subscribed: this.refs.smsConsent.checked,
          product_id: productId,
        }),
      });

      if (!response.ok) console.error('Coming soon: consent update failed', response.status);
      return response.ok;
    } catch (error) {
      console.error('Coming soon: consent update failed', error);
      return false;
    }
  }

  /**
   * @param {string} email
   * @returns {Promise<boolean>}
   */
  async #trackSubscribedEvent(email) {
    const { klaviyoCompanyId, productId } = this.dataset;
    if (!klaviyoCompanyId) return false;

    const shopify = /** @type {any} */ (window).Shopify;

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(klaviyoCompanyId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2025-01-15' },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      first_name: this.dataset.customerName || '',
                      properties: {
                        Coming_Soon_Product_Id: productId ? [productId] : [],
                      },
                    },
                  },
                },
                metric: {
                  data: { type: 'metric', attributes: { name: 'Subscribed to Launch' } },
                },
                properties: {
                  Country_Code: shopify?.country ?? 'AU',
                  Currency: shopify?.currency?.active ?? 'AUD',
                  Product_Name: this.dataset.productName,
                  Product_SKU: this.dataset.variantId,
                  Product_Handle: this.dataset.productHandle,
                  Product_Image_URL: this.dataset.productMedia,
                  Product_URL: window.location.href,
                  Launch_Date: this.dataset.launchDate,
                  Category: 'Sportstyle',
                  Brand: 'Salomon',
                  Subscription_Source: 'Product Page Modal',
                },
                time: new Date().toISOString(),
              },
            },
          }),
        }
      );

      if (response.status !== 202) console.error('Coming soon: Klaviyo event failed', response.status);
      return response.status === 202;
    } catch (error) {
      console.error('Coming soon: Klaviyo event failed', error);
      return false;
    }
  }

  #reset = () => {
    const { form, success, formError, emailError, email } = this.refs;
    form.hidden = false;
    success.hidden = true;
    formError.hidden = true;
    emailError.hidden = true;
    email.removeAttribute('aria-invalid');
  };
}

if (!customElements.get('coming-soon-notify-component')) {
  customElements.define('coming-soon-notify-component', ComingSoonNotify);
}
